import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PaymentInfoModal } from "./TopBar";
import { IdentityFields } from "./IdentityFields";
import { useQuickOffer } from "../hooks/useQuickOffer";
import { Loader2, ShieldCheck } from "lucide-react";

const QuickOfferPopup = ({
  isOpen,
  onClose,
  product,
  onSuccess,
  mode = "offer" // "offer" for bids/last-price, "direct_purchase" for buy/interest without negotiation
}) => {
  const [policyOpen, setPolicyOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");

  // Determine flavor: auction bid, auction buy-now (fixed price), or direct-sale interest (optional last price)
  const isAuctionBid = product?.sale_type === "auction" && mode !== "direct_purchase";
  const isAuctionBuyNow = product?.sale_type === "auction" && mode === "direct_purchase";
  const isDirectInterest = product?.sale_type !== "auction" && mode === "direct_purchase" && product?.accepts_offers !== false;
  const listedPrice = product?.promo_price || product?.buy_now_price || product?.price;

  const {
    loading, checkingWhatsapp, sessionWhatsapp, whatsapp, setWhatsapp, username, setUsername,
    confirmWhatsappChecked, setConfirmWhatsappChecked, userExists, maskedUsername, whatsappError,
    submitOffer
  } = useQuickOffer({ product, isOpen, onSuccess, onClose });

  // Fixed-price direct sale (accepts_offers === false): no editable field, always the listed price
  const isFixedPriceOnly = mode === "direct_purchase" && !isAuctionBuyNow && !isDirectInterest;

  // Reset the amount field whenever the popup opens
  useEffect(() => {
    if (isOpen) {
      if (isAuctionBuyNow || isFixedPriceOnly) {
        setOfferAmount(listedPrice?.toString() || "");
      } else {
        setOfferAmount(""); // direct-sale interest (optional) or auction bid (required)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product]);

  const currentBid = product?.current_bid || product?.price || 0;

  const handleSubmit = () => {
    const finalAmount = isDirectInterest
      ? (offerAmount ? parseFloat(offerAmount) : listedPrice)
      : parseFloat(offerAmount);
    submitOffer({ mode: isAuctionBid ? "offer" : "direct_purchase", amount: finalAmount });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "direct_purchase" ? "Me interesa este producto" : "Envía tu última oferta"}
          </DialogTitle>
          <DialogDescription>
            {isDirectInterest
              ? `Solicita "${product?.title}" al precio publicado o envía tu último precio`
              : mode === "direct_purchase"
                ? `Solicita "${product?.title}" al precio publicado`
                : product?.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Listed price info (shown for both buy-now and direct-sale interest) */}
          {mode === "direct_purchase" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-xs text-emerald-600 mb-1">Precio publicado</p>
              <p className="text-2xl font-bold text-emerald-700">${listedPrice?.toLocaleString()}</p>
              {product?.promo_price && (
                <p className="text-xs text-emerald-600 mt-1">
                  <span className="line-through text-[#94A3B8]">${product.price?.toLocaleString()}</span>
                  {" "}· -{product.promo_discount_percent}% de oferta activa
                </p>
              )}
            </div>
          )}

          {/* COD policy badge (tap to see full policy) */}
          <button
            type="button"
            onClick={() => setPolicyOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-[#FEF3C7] border border-amber-200 rounded-lg px-3 py-2"
            data-testid="quick-offer-cod-badge"
          >
            <ShieldCheck size={14} className="text-[#D97706] flex-shrink-0" />
            <span className="text-xs text-[#D97706] font-medium whitespace-nowrap truncate">
              Pago contraentrega: revisas y luego pagas →
            </span>
          </button>

          {/* Offer amount: required for auction bids, optional "último precio" for direct-sale interest */}
          {(isAuctionBid || isDirectInterest) && (
            <div>
              <Label htmlFor="offer-amount">
                {isAuctionBid ? "Precio ofertado ($)" : (
                  <>Último precio <span className="text-[#64748B] text-xs font-normal">(opcional)</span></>
                )}
              </Label>
              {isAuctionBid && (
                <p className="text-xs text-[#64748B] mb-1">
                  Oferta actual: <span className="font-semibold">${currentBid.toLocaleString()}</span>
                </p>
              )}
              <Input
                id="offer-amount"
                type="number"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder={isAuctionBid ? `Mínimo $${currentBid + 1}` : `$${listedPrice || 0}`}
                className="text-lg font-semibold"
                data-testid="quick-offer-amount"
              />
              {isDirectInterest && (
                <p className="text-xs text-[#64748B] mt-1">
                  Si lo dejas vacío, se solicitará al precio publicado: ${listedPrice?.toLocaleString()}
                </p>
              )}
              {isAuctionBid && (
                <p className="text-xs text-amber-600 mt-1">
                  💡 Sugerencia: Ofrece ${Math.round((product?.price || 0) * 0.95)} (5% menos) para mayor aceptación
                </p>
              )}
            </div>
          )}

          <IdentityFields
            testidPrefix="quick-offer"
            sessionWhatsapp={sessionWhatsapp}
            maskedUsername={maskedUsername}
            whatsapp={whatsapp}
            setWhatsapp={setWhatsapp}
            checkingWhatsapp={checkingWhatsapp}
            userExists={userExists}
            whatsappError={whatsappError}
            username={username}
            setUsername={setUsername}
            confirmWhatsappChecked={confirmWhatsappChecked}
            setConfirmWhatsappChecked={setConfirmWhatsappChecked}
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={loading || whatsapp.length !== 8 || (userExists === false && (!username || !confirmWhatsappChecked))}
          className="w-full bg-[#34D399] hover:bg-[#10B981] text-white"
          data-testid="quick-offer-submit"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            "Enviar solicitud"
          )}
        </Button>

        <p className="text-xs text-center text-[#94A3B8] mt-2">
          Al enviar, aceptas recibir notificaciones en tu WhatsApp
        </p>

        <PaymentInfoModal open={policyOpen} onOpenChange={setPolicyOpen} />
      </DialogContent>
    </Dialog>
  );
};

export default QuickOfferPopup;
